import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useLocation } from 'react-router-dom';
import { MagnifyingGlassMinus, MagnifyingGlassPlus, Stack, X, CaretDown } from '@phosphor-icons/react';
import { useIdea } from '../../context/IdeaContext';
import { useProject } from '../../context/ProjectContext';
import { CardFocus, CardInstance, IdeaCard } from '../../types';
import { STRUCTURE_TEMPLATES, StructureTemplate } from '../../lib/structureTemplates';
import { ConfirmModal } from '../ui/modal';
import { readableTextOn } from '../../lib/beatColors';
import { useScriptScenes, OutlineScene } from './useScriptScenes';

// ═══════════════════════════════════════════════════════════════════════════════
// OUTLINE STRIP - page-scaled outline lanes over the real screenplay
// (Final Draft 13's Outline Editor). An appearance's page goal and length place it;
// appearances without one show dashed, in order, until they are placed.
// ═══════════════════════════════════════════════════════════════════════════════

type Lane = 'macro' | 'micro';

const LANES: { lane: Lane; label: string; hint: string }[] = [
    { lane: 'macro', label: 'Acts', hint: 'Drag a card here as an act or long span' },
    { lane: 'micro', label: 'Beats', hint: 'Drag a card here as a beat or sequence' }
];

const GUTTER = 64;
const ROW_HEIGHT = 22;
const ROW_GAP = 3;
const LANE_PAD = 5;
const PPP_DEFAULT = 8;   // pixels per page
const PPP_MIN = 3;
const PPP_MAX = 40;
const SNAP = 0.25;       // quarter pages, like Final Draft's "35¾"
const MIN_LENGTH = 0.25;
const UNPLACED_SHARE: Record<Lane, number> = { macro: 0.25, micro: 0.04 };

const FOCUS_ACCENT: Record<CardFocus, string> = {
    note: 'var(--color-gray-500)',
    scene: 'var(--color-place)',
    character: 'var(--color-entity)',
    prop: 'var(--color-thing)',
    prop_significance: 'var(--color-significance)',
    relationship: 'var(--color-relationship)'
};

const snapPages = (pages: number) => Math.round(pages / SNAP) * SNAP;

// Length a newly placed appearance gets in a lane
export const outlineDefaultLength = (lane: Lane, targetPages: number) =>
    Math.max(MIN_LENGTH, snapPages(targetPages * UNPLACED_SHARE[lane]));

// Lanes mark themselves with data-outline-lane / data-ppp so other views (the Beat Board)
// can drop onto them without going through dnd-kit.
export const outlineLaneAt = (clientX: number, clientY: number): { lane: Lane; page: number; element: HTMLElement } | null => {
    const element = (document.elementFromPoint(clientX, clientY) as HTMLElement | null)?.closest<HTMLElement>('[data-outline-lane]');
    if (!element) return null;
    const ppp = Number(element.dataset.ppp) || PPP_DEFAULT;
    const page = Math.max(0, snapPages((clientX - element.getBoundingClientRect().left) / ppp));
    return { lane: element.dataset.outlineLane as Lane, page, element };
};

// 34.75 -> "35¾" (pages are 1-based for people; positions start at 0)
export const formatPage = (position: number) => {
    const page = position + 1;
    const whole = Math.floor(page + 1e-6);
    const fraction = ['', '¼', '½', '¾'][Math.round((page - whole) / SNAP) % 4];
    return `${whole}${fraction}`;
};

interface Bar {
    instance: CardInstance;
    card: IdeaCard;
    start: number;
    length: number;
    placed: boolean;
    row: number;
}

type Drag = { instanceId: string; mode: 'move' | 'start' | 'end'; pointerX: number; origin: { start: number; length: number } };

interface OutlineStripProps {
    canEdit: boolean;
}

export const OutlineStrip: React.FC<OutlineStripProps> = ({ canEdit }) => {
    const {
        currentIdea, cards, workspaceState, updateIdea, updateInstance, removeInstance,
        selectInstance, applyStructureTemplate, isSaving
    } = useIdea();
    const { selectedProjectId } = useProject();
    const location = useLocation();
    const script = useScriptScenes(selectedProjectId, location.pathname);

    const [ppp, setPpp] = useState(PPP_DEFAULT);
    const [drag, setDrag] = useState<Drag | null>(null);
    const [draft, setDraft] = useState<{ instanceId: string; start: number; length: number } | null>(null);
    const [templatesOpen, setTemplatesOpen] = useState(false);
    const [pendingTemplate, setPendingTemplate] = useState<StructureTemplate | null>(null);
    const [targetInput, setTargetInput] = useState('');

    const scrollRef = useRef<HTMLDivElement>(null);
    const pppRef = useRef(ppp);
    pppRef.current = ppp;
    const draftRef = useRef(draft);
    draftRef.current = draft;
    const movedRef = useRef(false);

    const target = currentIdea?.outline_target_pages ?? 120;
    useEffect(() => setTargetInput(String(target)), [target]);

    // Bars per lane: placed by page goal, unplaced laid out in order after them; overlaps stack
    const barsByLane = useMemo(() => {
        const result: Record<Lane, Bar[]> = { macro: [], micro: [] };
        for (const { lane } of LANES) {
            const entries = cards.flatMap(card => (card.instances ?? [])
                .filter(i => i.lane === lane)
                .map(instance => ({ instance, card })));
            const placed = entries.filter(e => e.instance.page_goal != null);
            const unplaced = entries.filter(e => e.instance.page_goal == null)
                .sort((a, b) => (a.instance.order_index ?? 0) - (b.instance.order_index ?? 0));

            const bars: Omit<Bar, 'row'>[] = placed.map(({ instance, card }) => ({
                instance, card, placed: true,
                start: instance.page_goal!,
                length: Math.max(MIN_LENGTH, instance.page_length ?? target * UNPLACED_SHARE[lane])
            }));
            let cursor = bars.reduce((end, bar) => Math.max(end, bar.start + bar.length), 0);
            for (const { instance, card } of unplaced) {
                const length = Math.max(MIN_LENGTH, snapPages(target * UNPLACED_SHARE[lane]));
                bars.push({ instance, card, placed: false, start: cursor, length });
                cursor += length;
            }

            const rowEnds: number[] = [];
            result[lane] = bars
                .sort((a, b) => a.start - b.start)
                .map(bar => {
                    let row = rowEnds.findIndex(end => end <= bar.start + 1e-6);
                    if (row === -1) row = rowEnds.length;
                    rowEnds[row] = bar.start + bar.length;
                    return { ...bar, row };
                });
        }
        return result;
    }, [cards, target]);

    const extentPages = useMemo(() => {
        const ends = [...barsByLane.macro, ...barsByLane.micro].map(b => b.start + b.length);
        return Math.ceil(Math.max(target, script.pageCount, ...ends) + 10);
    }, [barsByLane, script.pageCount, target]);

    const geometryOf = (bar: Bar) => draft?.instanceId === bar.instance.id ? draft : { start: bar.start, length: bar.length };

    // Pointer tracking for move / resize
    useEffect(() => {
        if (!drag) return;
        const onMove = (event: PointerEvent) => {
            const dx = event.clientX - drag.pointerX;
            if (!movedRef.current && Math.abs(dx) < 3) return;
            movedRef.current = true;
            const dPages = dx / pppRef.current;
            const { start, length } = drag.origin;
            let next = { start, length };
            if (drag.mode === 'move') next = { start: Math.max(0, snapPages(start + dPages)), length };
            if (drag.mode === 'end') next = { start, length: Math.max(MIN_LENGTH, snapPages(length + dPages)) };
            if (drag.mode === 'start') {
                const end = start + length;
                const newStart = Math.min(end - MIN_LENGTH, Math.max(0, snapPages(start + dPages)));
                next = { start: newStart, length: end - newStart };
            }
            setDraft({ instanceId: drag.instanceId, ...next });
        };
        const onUp = () => {
            const pending = draftRef.current;
            if (!movedRef.current) {
                selectInstance(drag.instanceId);
            } else if (pending && pending.instanceId === drag.instanceId) {
                updateInstance(drag.instanceId, { page_goal: pending.start, page_length: pending.length });
            }
            setDrag(null);
            setDraft(null);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
    }, [drag, selectInstance, updateInstance]);

    // Ctrl/Cmd + wheel zooms, keeping the page under the pointer in place
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const onWheel = (event: WheelEvent) => {
            if (!(event.ctrlKey || event.metaKey)) return;
            event.preventDefault();
            const rect = el.getBoundingClientRect();
            const pointer = event.clientX - rect.left - GUTTER;
            const page = (el.scrollLeft + pointer) / pppRef.current;
            const next = Math.min(PPP_MAX, Math.max(PPP_MIN, pppRef.current * Math.exp(-event.deltaY * 0.003)));
            setPpp(next);
            requestAnimationFrame(() => { el.scrollLeft = page * next - pointer; });
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, []);

    const startDrag = (event: React.PointerEvent, bar: Bar, mode: Drag['mode']) => {
        if (event.button !== 0) return;
        event.stopPropagation();
        movedRef.current = false;
        if (!canEdit) {
            selectInstance(bar.instance.id);
            return;
        }
        setDrag({ instanceId: bar.instance.id, mode, pointerX: event.clientX, origin: { start: bar.start, length: bar.length } });
    };

    const commitTarget = () => {
        const value = Math.round(Number(targetInput));
        if (!Number.isFinite(value) || value < 1 || value > 1000) {
            setTargetInput(String(target));
            return;
        }
        if (value !== target) updateIdea({ outline_target_pages: value });
    };

    const rulerTicks = useMemo(() => {
        const step = ppp >= 20 ? 1 : ppp >= 10 ? 5 : 10;
        const ticks: number[] = [];
        for (let page = 1; page <= extentPages; page += page === 1 && step > 1 ? step - 1 : step) ticks.push(page);
        return ticks;
    }, [ppp, extentPages]);

    const selectedInstanceId = workspaceState.selectedInstanceId;
    const selectedCardId = workspaceState.selectedCardId;
    const contentWidth = extentPages * ppp;

    return (
        <div className="flex flex-col h-full bg-carbon">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-3 py-1.5 border-b border-iron/40 bg-zinc-900">
                <div className="flex items-center gap-3">
                    <span className="text-[11px] font-semibold text-neutral-200 tracking-wide">Outline</span>
                    <label className="flex items-center gap-1.5 text-[10px] text-gray-500" title="Target length: the ruler and over-length marker follow this">
                        Target
                        <input
                            type="number"
                            min={1}
                            max={1000}
                            value={targetInput}
                            disabled={!canEdit}
                            onChange={(e) => setTargetInput(e.target.value)}
                            onBlur={commitTarget}
                            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                            className="w-12 px-1.5 py-0.5 rounded bg-onyx border border-iron/40 text-[10px] text-neutral-200 outline-none focus:border-slate-blue/60 disabled:opacity-60"
                        />
                        pages
                    </label>
                    {script.pageCount > 0 && (
                        <span className={`text-[10px] ${script.pageCount > target ? 'text-red-400' : 'text-gray-500'}`}>
                            Script: {script.pageCount} {script.pageCount === 1 ? 'page' : 'pages'}
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-1">
                    {canEdit && (
                        <div className="relative">
                            <button
                                onClick={() => setTemplatesOpen(open => !open)}
                                disabled={isSaving}
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium text-slate-blue-soft bg-slate-blue/10 border border-slate-blue/25 hover:bg-slate-blue/20 disabled:opacity-50"
                            >
                                <Stack className="w-3.5 h-3.5" />
                                Templates
                                <CaretDown className="w-3 h-3" />
                            </button>
                            {templatesOpen && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setTemplatesOpen(false)} />
                                    <div className="absolute right-0 bottom-full mb-1 z-50 w-80 max-h-80 overflow-y-auto custom-scrollbar rounded-lg bg-zinc-900 border border-iron/50 shadow-2xl p-1">
                                        {STRUCTURE_TEMPLATES.map(template => (
                                            <button
                                                key={template.id}
                                                onClick={() => {
                                                    setTemplatesOpen(false);
                                                    setPendingTemplate(template);
                                                }}
                                                className="w-full text-left px-3 py-2 rounded hover:bg-graphite"
                                            >
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="text-[12px] font-medium text-neutral-200">{template.name}</span>
                                                    <span className="text-[10px] text-gray-500 flex-shrink-0">{template.beats.length} beats · {template.targetPages} pp</span>
                                                </div>
                                                <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{template.summary}</p>
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    <div className="w-px h-4 bg-iron/50 mx-1" />
                    <button
                        onClick={() => setPpp(p => Math.max(PPP_MIN, p / 1.4))}
                        disabled={ppp <= PPP_MIN}
                        className="p-1.5 rounded text-gray-500 hover:text-gray-400 hover:bg-graphite/50 disabled:opacity-30"
                        title="Zoom out (Ctrl + wheel)"
                    >
                        <MagnifyingGlassMinus className="w-3.5 h-3.5" />
                    </button>
                    <button
                        onClick={() => setPpp(p => Math.min(PPP_MAX, p * 1.4))}
                        disabled={ppp >= PPP_MAX}
                        className="p-1.5 rounded text-gray-500 hover:text-gray-400 hover:bg-graphite/50 disabled:opacity-30"
                        title="Zoom in (Ctrl + wheel)"
                    >
                        <MagnifyingGlassPlus className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>

            {/* Lanes */}
            <div ref={scrollRef} className="relative flex-1 min-h-0 overflow-auto custom-scrollbar bg-onyx">
                <div className="relative" style={{ width: GUTTER + contentWidth, minWidth: '100%' }}>
                    {/* Ruler */}
                    <div className="sticky top-0 z-20 flex h-6 bg-zinc-900 border-b border-iron/40">
                        <div className="sticky left-0 z-10 flex-shrink-0 bg-zinc-900 border-r border-iron/30" style={{ width: GUTTER }} />
                        <div className="relative flex-1">
                            {rulerTicks.map(page => (
                                <div key={page} className="absolute top-0 bottom-0 flex items-end pb-0.5" style={{ left: (page - 1) * ppp }}>
                                    <div className="absolute left-0 bottom-0 h-2 w-px bg-iron/60" />
                                    <span className="ml-1 text-[9px] text-gray-500 font-mono">{page}</span>
                                </div>
                            ))}
                            <div className="absolute top-0 bottom-0 w-px bg-slate-blue" style={{ left: target * ppp }} title={`Target: ${target} pages`} />
                        </div>
                    </div>

                    {LANES.map(({ lane, label, hint }) => (
                        <OutlineLane
                            key={lane}
                            lane={lane}
                            label={label}
                            hint={hint}
                            bars={barsByLane[lane]}
                            ppp={ppp}
                            pppRef={pppRef}
                            target={target}
                            canEdit={canEdit}
                            selectedInstanceId={selectedInstanceId}
                            selectedCardId={selectedCardId}
                            geometryOf={geometryOf}
                            onBarPointerDown={startDrag}
                            onRemove={(id) => removeInstance(id)}
                        />
                    ))}

                    <ScriptLane scenes={script.scenes} ppp={ppp} target={target} />
                </div>
            </div>

            <ConfirmModal
                isOpen={pendingTemplate !== null}
                onClose={() => setPendingTemplate(null)}
                onConfirm={() => {
                    if (pendingTemplate) applyStructureTemplate(pendingTemplate);
                    setPendingTemplate(null);
                }}
                title={pendingTemplate ? `Add ${pendingTemplate.name}?` : ''}
                description={pendingTemplate
                    ? `Adds ${pendingTemplate.beats.length} beats to this outline and the Beat Board. Your existing cards stay as they are.`
                    : ''}
                confirmText="Add beats"
            />
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// OUTLINE LANE - one outline level; cards from the library drop in at the pointer's page
// ═══════════════════════════════════════════════════════════════════════════════

interface OutlineLaneProps {
    lane: Lane;
    label: string;
    hint: string;
    bars: Bar[];
    ppp: number;
    pppRef: React.MutableRefObject<number>;
    target: number;
    canEdit: boolean;
    selectedInstanceId: string | null;
    selectedCardId: string | null;
    geometryOf: (bar: Bar) => { start: number; length: number };
    onBarPointerDown: (event: React.PointerEvent, bar: Bar, mode: Drag['mode']) => void;
    onRemove: (instanceId: string) => void;
}

const OutlineLane: React.FC<OutlineLaneProps> = ({
    lane, label, hint, bars, ppp, pppRef, target, canEdit,
    selectedInstanceId, selectedCardId, geometryOf, onBarPointerDown, onRemove
}) => {
    const contentRef = useRef<HTMLDivElement | null>(null);

    // The workspace drop handler asks the lane which page sits under the dropped card
    const pageAt = useCallback((clientX: number) => {
        const left = contentRef.current?.getBoundingClientRect().left ?? 0;
        return Math.max(0, snapPages((clientX - left) / pppRef.current));
    }, [pppRef]);

    const { setNodeRef, isOver } = useDroppable({
        id: `outline-lane-${lane}`,
        data: { type: 'timeline-lane', lane, pageAt, defaultLength: (targetPages: number) => outlineDefaultLength(lane, targetPages) },
        disabled: !canEdit
    });

    const rows = Math.max(1, ...bars.map(b => b.row + 1));
    const height = LANE_PAD * 2 + rows * ROW_HEIGHT + (rows - 1) * ROW_GAP;

    return (
        <div className={`relative flex border-b border-iron/30 ${isOver ? 'bg-slate-blue/8' : ''}`} style={{ height }}>
            <div className="sticky left-0 z-10 flex-shrink-0 flex items-center px-2 bg-zinc-900 border-r border-iron/30" style={{ width: GUTTER }}>
                <span className="text-[9px] font-semibold uppercase tracking-wider text-gray-400">{label}</span>
            </div>
            <div
                ref={(node) => { contentRef.current = node; setNodeRef(node); }}
                data-outline-lane={canEdit ? lane : undefined}
                data-ppp={ppp}
                className="relative flex-1 data-[drop-active=true]:bg-slate-blue/10"
                style={{ backgroundImage: 'linear-gradient(to right, color-mix(in oklab, var(--color-iron) 25%, transparent) 1px, transparent 1px)', backgroundSize: `${ppp * 10}px 100%` }}
            >
                {/* Past the target length */}
                <div className="absolute top-0 bottom-0 right-0 bg-red-500/5 border-l border-slate-blue/70 pointer-events-none" style={{ left: target * ppp }} />

                {bars.length === 0 && (
                    <div className="absolute inset-0 flex items-center pl-3 pointer-events-none">
                        <span className="text-[10px] text-gray-500/70">{canEdit ? hint : 'Nothing here yet'}</span>
                    </div>
                )}

                {bars.map(bar => {
                    const { start, length } = geometryOf(bar);
                    const isSelected = bar.instance.id === selectedInstanceId || (!selectedInstanceId && bar.card.id === selectedCardId);
                    const fill = bar.card.color || null;
                    const accent = FOCUS_ACCENT[bar.card.focus] ?? FOCUS_ACCENT.note;
                    const width = Math.max(6, length * ppp);
                    const title = bar.card.title || 'Untitled';
                    return (
                        <div
                            key={bar.instance.id}
                            className={`group absolute rounded-sm flex items-center overflow-hidden select-none
                                ${canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}
                                ${isSelected ? 'ring-2 ring-slate-blue z-10' : ''}`}
                            style={{
                                left: start * ppp,
                                width,
                                top: LANE_PAD + bar.row * (ROW_HEIGHT + ROW_GAP),
                                height: ROW_HEIGHT,
                                background: fill ?? `color-mix(in oklab, ${accent} 30%, transparent)`,
                                color: fill ? readableTextOn(fill) : undefined,
                                border: bar.placed
                                    ? `1px solid ${fill ? 'transparent' : `color-mix(in oklab, ${accent} 70%, transparent)`}`
                                    : `1px dashed ${fill ? 'currentColor' : accent}`,
                                opacity: bar.placed ? 1 : 0.75
                            }}
                            title={bar.placed
                                ? `${title} · p${formatPage(start)}–${formatPage(start + length)}`
                                : `${title} · no page target yet — drag to place`}
                            onPointerDown={(e) => onBarPointerDown(e, bar, 'move')}
                        >
                            {canEdit && (
                                <div className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize z-10" onPointerDown={(e) => onBarPointerDown(e, bar, 'start')} />
                            )}
                            <span className={`px-1.5 text-[10px] font-medium truncate ${fill ? '' : 'text-neutral-200'}`}>{title}</span>
                            {width > 110 && bar.placed && (
                                <span className={`ml-auto pr-1.5 text-[9px] italic whitespace-nowrap ${fill ? 'opacity-70' : 'text-gray-400'}`}>
                                    {formatPage(start)}–{formatPage(start + length)}
                                </span>
                            )}
                            {canEdit && (
                                <>
                                    <button
                                        className="absolute right-1.5 top-1/2 -translate-y-1/2 hidden group-hover:flex w-3.5 h-3.5 items-center justify-center rounded-sm bg-black/40 text-white z-20"
                                        title="Remove from outline"
                                        onPointerDown={(e) => e.stopPropagation()}
                                        onClick={() => onRemove(bar.instance.id)}
                                    >
                                        <X className="w-2.5 h-2.5" weight="bold" />
                                    </button>
                                    <div className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize z-10" onPointerDown={(e) => onBarPointerDown(e, bar, 'end')} />
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SCRIPT LANE - scenes from the Story page, sized by their page length
// ═══════════════════════════════════════════════════════════════════════════════

const ScriptLane: React.FC<{ scenes: OutlineScene[]; ppp: number; target: number }> = ({ scenes, ppp, target }) => (
    <div className="relative flex" style={{ height: ROW_HEIGHT + LANE_PAD * 2 }}>
        <div className="sticky left-0 z-10 flex-shrink-0 flex items-center px-2 bg-zinc-900 border-r border-iron/30" style={{ width: GUTTER }}>
            <span className="text-[9px] font-semibold uppercase tracking-wider text-gray-400">Script</span>
        </div>
        <div className="relative flex-1">
            <div className="absolute top-0 bottom-0 right-0 bg-red-500/5 border-l border-slate-blue/70 pointer-events-none" style={{ left: target * ppp }} />
            {scenes.length === 0 ? (
                <div className="absolute inset-0 flex items-center pl-3 pointer-events-none">
                    <span className="text-[10px] text-gray-500/70">Scenes from your screenplay appear here</span>
                </div>
            ) : scenes.map((scene, index) => (
                <div
                    key={scene.blockId}
                    className={`absolute rounded-sm border border-iron/50 flex items-center overflow-hidden ${index % 2 ? 'bg-graphite' : 'bg-zinc-800'}`}
                    style={{ left: scene.start * ppp, width: Math.max(3, scene.length * ppp - 1), top: LANE_PAD, height: ROW_HEIGHT }}
                    title={`${scene.heading} · p${formatPage(scene.start)}–${formatPage(scene.start + scene.length)}`}
                >
                    <span className="px-1 text-[9px] font-mono uppercase text-gray-400 truncate">{scene.heading}</span>
                </div>
            ))}
        </div>
    </div>
);
